import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as S from "./styles";
import * as API from "../../api";
import { InputDate } from "../InputDate";
import { InputTextArea } from "../InputTextArea";
import { InputList } from "../InputList";
import { InputText } from "../InputText";
import { InputNumber } from "../InputNumber";
import { Button } from "../Button";
import { ModuleTareaDocsReserva } from "../ModuleTareaDocsReserva";
import { ModalDocumentos } from "../ModalDocumentos";
import { Modal } from "../Modal";
import { METADATA_DOCS_RESERVA } from "../../constants";

// IdTipo mapping from PostFirmaTipoHito table
export const TIPO_HITO = {
  RESERVA: 1,
  CONTRATO_PRIVADO: 2,
  RESCISION_RESERVA: 3,
  RESCISION_CONTRATO_PRIVADO: 4,
  ESCRITURA: 5,
  ANULACION_SUBSANACION: 6,
};

export const NOMBRE_HITO = {
  1: "Reserva",
  2: "Contrato Privado",
  3: "Rescisión de reserva",
  4: "Rescisión de contrato privado",
  5: "Escritura",
  6: "Anulación/Subsanación Escritura",
};

// Hitos obligatorios (1=Reserva, 2=ContratoPrivado, 5=Escritura)
export const HITOS_OBLIGATORIOS = [
  TIPO_HITO.RESERVA,
  TIPO_HITO.CONTRATO_PRIVADO,
  TIPO_HITO.ESCRITURA,
];

// DNE2E-131 (T3.1/T3.2 + correos): tipos de documento (TipoDocumento) que se suben por hito.
// Negocio eliminó el campo "Importe justificante" -> el justificante (59) ya no se exige ni se muestra.
export const DOC_TIPOS_ESCRITURA = [21, 22]; // Copia simple, Cheque de venta
export const DOC_TIPOS_CONTRATO_PRIVADO = [63]; // Contrato privado

// DNE2E-131 (correos): etiquetas legibles de los tipos de documento (para la leyenda de requeridos).
export const DOC_TIPO_LABELS = {
  21: "Copia simple",
  22: "Cheque de venta",
  63: "Contrato privado",
};

// DNE2E-131 (correos): desglose de cheque/transferencia hasta 6 importes.
export const CHEQUE_FIELDS = [
  "importeChequeTransferencia",
  "importeChequeTransferencia2",
  "importeChequeTransferencia3",
  "importeChequeTransferencia4",
  "importeChequeTransferencia5",
  "importeChequeTransferencia6",
];

const FAKE_OPTIONS = {
  dobleFiscalidad: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
  comunidadBienes: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
  ventaAplazada: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
  subsanar: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
  tributacionEscritura: [
    { value: "1", label: "IVA" },
    { value: "2", label: "ITP" },
  ],
  financiacion: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
  retencionProvisionFondos: [
    { value: "1", label: "Si" },
    { value: "0", label: "No" },
  ],
};

const EMPTY_HITO = {
  fecComunicacionFinanzas: "",
  fecSubidaDocPrinex: "",
  completado: false,
  observaciones: "",
  adendas: [],
};

const getHitoByTipo = (hitos, idTipo) =>
  hitos.find((h) => h.idTipo === idTipo) || null;

const formatImporte = (valor) => {
  if (valor == null || valor === "" || valor === undefined)
    return "0,00 EUR";
  // Si el valor ya es un string formateado con EUR, devolverlo tal cual
  if (typeof valor === "string" && valor.includes("EUR")) return valor;
  const num = Number(valor);
  if (isNaN(num)) return "0,00 EUR";
  return num.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " EUR";
};

// Convierte fecha del backend a formato datetime-local (yyyy-MM-ddTHH:mm)
const toDatetimeLocal = (val) => {
  if (!val || val === "") return "";
  // Si ya viene en formato ISO (yyyy-MM-ddTHH:mm), devolverlo tal cual
  if (val.length >= 16 && val[4] === "-" && val[10] === "T") {
    return val.substring(0, 16);
  }
  // Intentar parsear otras formas
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

// Convierte fecha del backend a formato solo fecha (yyyy-MM-dd)
const toDateOnly = (val) => {
  if (!val || val === "") return "";
  // Si ya viene en formato ISO, extraer solo la parte de fecha
  if (val.length >= 10 && val[4] === "-" && val[7] === "-") {
    return val.substring(0, 10);
  }
  // Intentar parsear otras formas
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

// Convierte fecha del input datetime-local a ISO string para el backend
const toISOForBackend = (val) => {
  if (!val || val === "") return null;
  // datetime-local da "yyyy-MM-ddTHH:mm", el backend acepta DateTime? en JSON
  return val;
};

const puedeCompletar = (idTipo, todosHitos) => {
  // Anulación/Subsanación Escritura solo puede completarse una vez completada la Escritura
  if (idTipo === TIPO_HITO.ANULACION_SUBSANACION) {
    const hitoEscritura = todosHitos.find((h) => h.idTipo === TIPO_HITO.ESCRITURA);
    return !!hitoEscritura && !!hitoEscritura.completado;
  }
  if (!HITOS_OBLIGATORIOS.includes(idTipo)) return true;
  const idx = HITOS_OBLIGATORIOS.indexOf(idTipo);
  if (idx <= 0) return true;
  // Verificar que TODOS los hitos obligatorios anteriores estén completados
  // Si un hito obligatorio anterior no existe (no fue creado por el backend), se omite
  for (let i = 0; i < idx; i++) {
    const tipoAnterior = HITOS_OBLIGATORIOS[i];
    const hitoAnterior = todosHitos.find((h) => h.idTipo === tipoAnterior);
    if (!hitoAnterior) continue; // Hito no creado por backend, no bloquear
    if (!hitoAnterior.completado) return false;
  }
  return true;
};

// Validación de campos antes de completar un hito
const validarCamposParaCompletar = (idTipo, hito) => {
  const errores = [];
  const nombre = NOMBRE_HITO[idTipo] || "Hito";

  // Hito 6 (Anulación/Subsanación) no tiene habilitados los campos de fechas comunes
  // Los demás hitos (incluidos Rescisión Reserva y Rescisión Contrato Privado) sí los requieren
  if (idTipo !== TIPO_HITO.ANULACION_SUBSANACION) {
    if (!hito.fecComunicacionFinanzas || hito.fecComunicacionFinanzas === "")
      errores.push("Fecha comunicación finanzas es obligatoria");
    if (!hito.fecSubidaDocPrinex || hito.fecSubidaDocPrinex === "")
      errores.push("Fecha subida doc. Prinex es obligatoria");
  }

  // Campos específicos de Escritura (IdTipo = 5)
  if (idTipo === TIPO_HITO.ESCRITURA) {
    if (hito.dobleFiscalidad == null || hito.dobleFiscalidad === "")
      errores.push("Doble Fiscalidad es obligatorio");
    if (hito.comunidadDeBienes == null || hito.comunidadDeBienes === "")
      errores.push("Comunidad de Bienes es obligatorio");
    if (hito.importeCVAplazadoEnEscritura == null || hito.importeCVAplazadoEnEscritura === "")
      errores.push("Importe CV Aplazado es obligatorio");
    if (hito.importeChequeTransferencia == null || hito.importeChequeTransferencia === "")
      errores.push("Importe Cheque/Transferencia es obligatorio");
  }

  // Campos específicos de Contrato Privado (IdTipo = 2) - validar adendas con monto > 0
  if (idTipo === TIPO_HITO.CONTRATO_PRIVADO && hito.adendas) {
    hito.adendas.forEach((adenda) => {
      if (adenda.monto != null && Number(adenda.monto) > 0) {
        if (!adenda.completado)
          errores.push(`Adenda ${adenda.numeroAdenda} tiene monto > 0 y debe ser completada`);
        if (!adenda.fecComunicacionFinanzas || adenda.fecComunicacionFinanzas === "")
          errores.push(`Adenda ${adenda.numeroAdenda}: Fecha comunicación finanzas es obligatoria`);
        if (!adenda.fecSubidaDocPrinex || adenda.fecSubidaDocPrinex === "")
          errores.push(`Adenda ${adenda.numeroAdenda}: Fecha subida doc. Prinex es obligatoria`);
      }
    });
  }

  // Campos específicos de Anulación/Subsanación (IdTipo = 6) - todos obligatorios
  if (idTipo === TIPO_HITO.ANULACION_SUBSANACION) {
    if (!hito.subsanar || hito.subsanar === "")
      errores.push("Subsanar es obligatorio");
    if (hito.subsanar === "1" && (!hito.fechaSubsanada || hito.fechaSubsanada === ""))
      errores.push("Fecha subsanada es obligatoria cuando Subsanar es Sí");
    if (!hito.tributacionEscritura || hito.tributacionEscritura === "")
      errores.push("Tributación escritura es obligatorio");
    if (!hito.financiacion || hito.financiacion === "")
      errores.push("Financiación es obligatorio");
    if (!hito.retencionProvisionFondos || hito.retencionProvisionFondos === "")
      errores.push("Retención prov. fondos es obligatorio");
    if (!hito.clausulaIBI || hito.clausulaIBI === "")
      errores.push("Cláusula IBI es obligatorio");
    if (!hito.clausulaCCPP || hito.clausulaCCPP === "")
      errores.push("Cláusula CCPP es obligatorio");
    if (!hito.gestoriaTramiteCancelacion || hito.gestoriaTramiteCancelacion === "")
      errores.push("Gestoría cancelación es obligatorio");
  }

  return errores;
};

export const ModuleDatosPostFirma = ({
  hitos,
  datosGenerales,
  access,
  idTarea,
  onHitoSaved,
  guardarTodosRef,
  readOnly = false,
  documentosReserva = [],
  lists = {},
}) => {
  const [hitosState, setHitosState] = useState([]);
  const [saving, setSaving] = useState(null);
  const isSavingAll = saving === "all";
  const [subsanacionMode, setSubsanacionMode] = useState(null); // null | "modificar" | "anular"
  const [showSubsanacionModal, setShowSubsanacionModal] = useState(false);
  const [confirmCompletarHito, setConfirmCompletarHito] = useState(null); // null | idTipo
  const [clausulasIBI, setClausulasIBI] = useState([]);
  const [clausulasCCPP, setClausulasCCPP] = useState([]);
  // Paneles desplegados de la comparativa IA/E2E, por hito: "{idTipo}|{grupo}-{ordenEntidad}".
  const [panelesContraste, setPanelesContraste] = useState({});
  // DNE2E-131 (T3.1/T3.2): modal de alta de documento por hito (null | "escritura" | "contratoPrivado")
  const [docModal, setDocModal] = useState(null);
  // Documento pendiente de confirmar su eliminación (null | objeto documento).
  const [docDelete, setDocDelete] = useState(null);

 useEffect(() => {
    API.obtenerClausulasAnulacion()
      .then((res) => {

        setClausulasIBI(
          (res?.clausulasIBI || []).map((x) => ({ value: x.valor, label: x.etiqueta }))
        );

        setClausulasCCPP(
          (res?.clausulasCCPP || []).map((x) => ({ value: x.valor, label: x.etiqueta }))
        );
        
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (hitos && hitos.length > 0) {
      // Normalizar fechas del backend al formato datetime-local
      const hitosNormalizados = hitos.map((h) => ({
        ...h,
        fecComunicacionFinanzas: toDateOnly(h.fecComunicacionFinanzas),
        fecSubidaDocPrinex: toDateOnly(h.fecSubidaDocPrinex),
        fecCompletado: toDatetimeLocal(h.fecCompletado),
        fechaSubsanada: toDatetimeLocal(h.fechaSubsanada),
        // Fecha vencimiento (manual en el hito Contrato Privado)
        fechaVencimiento: toDateOnly(h.fechaVencimiento),
        // Fecha reserva/CP (manual en el hito Escritura, dato de la reserva)
        fechaReservaCpEscritura: toDateOnly(h.fechaReservaCpEscritura),
        adendas: (h.adendas || []).map((a) => ({
          ...a,
          fecComunicacionFinanzas: toDateOnly(a.fecComunicacionFinanzas),
          fecSubidaDocPrinex: toDateOnly(a.fecSubidaDocPrinex),
          fecCompletado: toDatetimeLocal(a.fecCompletado),
        })),
      }));
      setHitosState(hitosNormalizados);
    }
  }, [hitos]);

  const handleHitoChange = (idTipo, field, value) => {
    setHitosState((prev) =>
      prev.map((h) => (h.idTipo === idTipo ? { ...h, [field]: value } : h))
    );
  };

  // DNE2E-131 (rediseño): "Requiere Subsanación" POR CAMPO (Sí/No/Subsanado). Solo editable
  // cuando el campo está en KO; en OK queda fijo en "No".
  const handleSubsanacionChange = (idTipo, filaId, value) => {
    setHitosState((prev) =>
      prev.map((h) =>
        h.idTipo === idTipo
          ? {
              ...h,
              subsanaciones: (h.subsanaciones || []).map((s) =>
                s.id === filaId ? { ...s, requiereSubsanacion: value } : s
              ),
            }
          : h
      )
    );
  };

  // DNE2E-131 (correos): cambio de un importe de cheque. Al poner 0/vacío se limpian los
  // importes siguientes (quedan inhabilitados por no tener importe previo > 0).
  const handleChequeChange = (idTipo, index, value) => {
    setHitosState((prev) =>
      prev.map((h) => {
        if (h.idTipo !== idTipo) return h;
        const next = { ...h, [CHEQUE_FIELDS[index]]: value };
        const vacio = value === "" || value == null || Number(value) <= 0;
        if (vacio) {
          for (let i = index + 1; i < CHEQUE_FIELDS.length; i++) next[CHEQUE_FIELDS[i]] = "";
        }
        return next;
      })
    );
  };

  const handleHitoListChange = (idTipo, field, value) => {
    setHitosState((prev) =>
      prev.map((h) =>
        h.idTipo === idTipo ? { ...h, [field.name]: value } : h
      )
    );
  };

  const handleAdendaChange = (idTipo, adendaId, field, value) => {
    setHitosState((prev) =>
      prev.map((h) => {
        if (h.idTipo !== idTipo) return h;
        return {
          ...h,
          adendas: (h.adendas || []).map((a) =>
            a.id === adendaId ? { ...a, [field]: value } : a
          ),
        };
      })
    );
  };

  // DNE2E-131 (rediseño): bloque "Control de Subsanación" (Escritura y C.Privado).
  const OPCIONES_ESTADO_SUBSANACION = [
    "Pendiente",
    "En gestión",
    "Firmada escritura subsanación",
    "Resuelto",
  ];
  // DNE2E-131 (correos): leyenda visible de los documentos requeridos por el hito
  // (Escritura y Contrato Privado), indicando cuáles están subidos y cuáles faltan.
  // Estos documentos son necesarios para la lectura/contraste con IA.
  const renderDocsRequeridos = (hito, idTipo) => {
    const tipos =
      idTipo === TIPO_HITO.ESCRITURA
        ? DOC_TIPOS_ESCRITURA
        : idTipo === TIPO_HITO.CONTRATO_PRIVADO
        ? DOC_TIPOS_CONTRATO_PRIVADO
        : [];
    if (!tipos.length) return null;
    const subidos = new Set(
      docsDeHito(hito?.id, tipos).map((d) => Number(d.idTipoDocumento))
    );
    const box = {
      margin: "0.6rem 0",
      padding: "0.45rem 0.7rem",
      borderRadius: 6,
      border: "1px solid #ececec",
      background: "#fafafa",
      color: "#6b7280",
      fontSize: "0.8rem",
    };
    return (
      <div style={box}>
        <span style={{ color: "#8a8a8a" }}>
          ⓘ Documentos requeridos para la lectura IA:
        </span>{" "}
        {tipos.map((t, i) => {
          const ok = subidos.has(t);
          return (
            <span key={t}>
              {i > 0 && " · "}
              <span style={{ color: ok ? "#4b8b3b" : "#b58a2e" }}>
                {ok ? "✓" : "○"} {DOC_TIPO_LABELS[t] || `Tipo ${t}`}
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  const renderSubsanacion = (hito, idTipo, readOnly) => {
    if (!hito || !hito.id) return null;
    const filas = hito.subsanaciones || [];
    // Sin lectura de la IA no se muestra la comparativa ni la subsanación; en su lugar
    // un mensaje sutil indicando que hacen falta los documentos para la lectura IA.
    const hayLecturaIa =
      hito.iaProcesada === true ||
      filas.some((f) => f.valorIa != null && f.valorIa !== "");
    if (!hayLecturaIa) {
      const tipos =
        idTipo === TIPO_HITO.ESCRITURA ? DOC_TIPOS_ESCRITURA : DOC_TIPOS_CONTRATO_PRIVADO;
      const tiposSubidos = new Set(
        docsDeHito(hito.id, tipos).map((d) => Number(d.idTipoDocumento))
      );
      const faltanDocs = tipos.some((t) => !tiposSubidos.has(t));
      // Si faltan documentos, la leyenda de "Documentos requeridos" ya lo indica; aquí
      // solo se avisa cuando están todos subidos pero la IA aún no ha dejado la lectura.
      if (faltanDocs) return null;
      return (
        <p style={{ fontSize: "0.8rem", color: "#8a8a8a", fontStyle: "italic", margin: "0.75rem 0 0" }}>
          ⓘ Documentos subidos. Pendiente de la lectura de IA (se procesa en la carga nocturna).
        </p>
      );
    }
    const th = {
      textAlign: "left",
      padding: "6px 10px",
      border: "1px dotted #d9c6cd",
      fontWeight: "bold",
      fontSize: "0.8rem",
      color: "#6b5560",
      background: "#faf6f8",
    };
    const td = { padding: "6px 10px", border: "1px dotted #e6dbe0", fontSize: "0.82rem" };
    // Estilo "campo de solo lectura" para los valores (E2E e IA no son editables).
    const roVal = {
      display: "inline-block",
      minWidth: 80,
      maxWidth: 220,
      padding: "3px 8px",
      borderRadius: 4,
      border: "1px dotted #cfc7cb",
      background: "#f6f6f7",
      color: "#444",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
    const roValVacio = { ...roVal, color: "#b0b0b0", fontStyle: "italic" };
    // Parseo numérico tolerante (mismo criterio que el backend): admite separador decimal
    // "." o "," y separadores de miles; si aparecen ambos, el ÚLTIMO es el decimal.
    const parseNumTolerante = (s) => {
      if (s == null) return null;
      let t = String(s).trim().replace(/\s/g, "").replace("%", "").replace("€", "");
      if (t === "") return null;
      const lastDot = t.lastIndexOf("."), lastComma = t.lastIndexOf(",");
      if (lastDot >= 0 && lastComma >= 0) {
        if (lastComma > lastDot) t = t.replace(/\./g, "").replace(",", ".");
        else t = t.replace(/,/g, "");
      } else if (lastComma >= 0) {
        t = t.replace(",", ".");
      }
      const n = Number(t);
      return isNaN(n) ? null : n;
    };
    // ¿El texto parece un importe? Solo dígitos/separadores y TERMINA en parte decimal
    // (.NN o ,NN). Así formateamos importes aunque no llegue el tipo, sin tocar identificadores
    // (DNI, nº finca, etc., que no acaban en decimales).
    const pareceImporte = (s) => /^-?[\d.,\s]+$/.test(s) && /[.,]\d{1,2}$/.test(s);
    // Formatea el valor mostrado (E2E e IA). Numérico -> es-ES miles/2dec (por tipo, o autodetectado
    // si parece importe). Entero -> sin decimales ("Nº registro de la propiedad", "Nº pagos":
    // son contadores, no importes). Fecha -> dd/MM/yyyy (por tipo o patrón ISO). Resto -> tal cual.
    const fmtValor = (v, tipo) => {
      if (v == null || v === "") return v;
      const s = String(v).trim();
      if (tipo === "Fecha" || /^\d{4}-\d{2}-\d{2}/.test(s)) {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
      }
      if (tipo === "Entero") {
        const ne = parseNumTolerante(s);
        // Sin separador de miles: son un contador y un número de registro, no importes.
        if (ne != null) return String(Math.round(ne));
      }
      if (tipo === "Numerico" || (!tipo && pareceImporte(s))) {
        const n = parseNumTolerante(s);
        if (n != null) return n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return v; // Booleano / Texto
    };
    // `detalle` (opcional): texto original del lado E2E cuando el valor mostrado es una
    // interpretación del dato. Hoy lo usa "Cláusula Campaña", que se contrasta como Sí/No
    // según el campo esté informado: el tooltip enseña la redacción que hay en E2E.
    const celdaValor = (v, tipo, detalle) => {
      const fmt = fmtValor(v, tipo);
      const vacio = fmt == null || fmt === "";
      const hayDetalle = detalle != null && detalle !== "";
      const titulo = hayDetalle
        ? `Valor en E2E: ${detalle}`
        : vacio
        ? ""
        : String(fmt);
      return (
        <span
          style={
            hayDetalle
              ? { ...(vacio ? roValVacio : roVal), borderStyle: "dashed", cursor: "help" }
              : vacio
              ? roValVacio
              : roVal
          }
          title={titulo}
        >
          {vacio ? "—" : fmt}
          {hayDetalle && <span style={{ color: "#8a8a8a", marginLeft: 4 }}>ⓘ</span>}
        </span>
      );
    };
    const hayKo = filas.some((f) => f.estado === "KO");

    // Ajustes-contraste-postfirma.docx: la comparativa se presenta por PANELES desplegables,
    // cada uno con su propio estado (OK / No OK): un panel por comprador, uno por activo de la
    // reserva y uno con el resto de campos. Es igual en Escritura y en Contrato Privado; lo
    // único que cambia es el contenido del último panel, que el backend manda en el grupo
    // "Reserva" con el set de campos de cada hito.
    const tablaFilas = (filasPanel) => (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0.5rem" }}>
          <thead>
            <tr>
              <th style={th}>Campo</th>
              <th style={th}>Valor E2E</th>
              <th style={th}>Valor IA</th>
              <th style={th}>Estado</th>
              <th style={th}>Requiere Subsanación</th>
            </tr>
          </thead>
          <tbody>
            {filasPanel.map((f) => (
              <tr key={f.id}>
                <td style={{ ...td, fontWeight: "bold", color: "#555" }}>{f.campo}</td>
                {/* Valor E2E e IA son de solo lectura (E2E se edita en el formulario del
                    hito; la IA la deja el proceso de contraste). Se formatean según su tipo. */}
                <td style={td}>{celdaValor(f.valorE2E, f.tipoDato, f.valorE2EDetalle)}</td>
                <td style={td}>{celdaValor(f.valorIa, f.tipoDato)}</td>
                <td style={td}>
                  {f.estado ? (
                    <S.HitoStatusBadge className={f.estado === "OK" ? "completed" : "subsanacion"}>
                      {f.estado}
                    </S.HitoStatusBadge>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={td}>
                  {/* DNE2E-131: editable solo si el contraste es KO. En OK -> "No". Sin estado
                      (la IA no ha leído el campo) -> "-", igual que la columna Estado: decir "No"
                      ahí daba a entender que el campo se había validado (negocio, 24-sep-2026). */}
                  {f.estado === "KO" ? (
                    <select
                      value={f.requiereSubsanacion || "Sí"}
                      disabled={readOnly}
                      onChange={(e) =>
                        handleSubsanacionChange(idTipo, f.id, e.target.value)
                      }
                      style={{
                        padding: "3px 6px",
                        borderRadius: 4,
                        border: "1px solid #d9c6cd",
                        fontSize: "0.82rem",
                        background: readOnly ? "#f0f0f0" : "#fff",
                      }}
                    >
                      <option value="Sí">Sí</option>
                      <option value="No">No</option>
                      <option value="Subsanado">Subsanado</option>
                    </select>
                  ) : f.estado ? (
                    "No"
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    // Filas de un grupo agrupadas por panel. La agrupación es por ENTIDAD (idEntidad), que es
    // la identidad que usa el backend, y solo cae al orden cuando la entidad no existe en E2E
    // (paneles que solo ha leído la IA). Si se agrupara solo por ordenEntidad, dos entidades
    // que compartieran ese número se fusionarían en un panel y los campos saldrían duplicados.
    const panelesDelGrupo = (grupo) => {
      const mapa = new Map();
      filas
        .filter((f) => (f.grupo || "Reserva") === grupo)
        .forEach((f) => {
          const ord = f.ordenEntidad == null ? 0 : f.ordenEntidad;
          const clave = f.idEntidad ? `e-${f.idEntidad}` : `o-${ord}`;
          if (!mapa.has(clave))
            mapa.set(clave, { clave: `${grupo}-${clave}`, orden: ord, nombre: f.nombreEntidad, filas: [] });
          mapa.get(clave).filas.push(f);
        });
      return Array.from(mapa.values()).sort((a, b) => a.orden - b.orden);
    };

    // Estado del panel: No OK si algún campo discrepa; OK si ya hay contraste y ninguno discrepa;
    // sin badge mientras la IA no haya dejado valores de ese panel.
    const estadoPanel = (filasPanel) => {
      if (filasPanel.some((f) => f.estado === "KO")) return "No OK";
      if (filasPanel.some((f) => f.estado === "OK")) return "OK";
      return null;
    };

    const renderPanel = (clave, titulo, filasPanel, extra) => {
      const idPanel = `${idTipo}|${clave}`;
      const abierto = !!panelesContraste[idPanel];
      const est = estadoPanel(filasPanel);
      return (
        <div
          key={clave}
          style={{ borderTop: "1px solid #ececec", background: abierto ? "#fdf7f9" : "#fff" }}
        >
          <div
            onClick={() =>
              setPanelesContraste((prev) => ({ ...prev, [idPanel]: !prev[idPanel] }))
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              padding: "0.5rem 0.75rem",
            }}
          >
            <span style={{ color: "#8a8a8a", fontSize: "0.8rem" }}>{abierto ? "⌄" : "›"}</span>
            <span style={{ fontWeight: "bold", fontSize: "0.85rem", color: "#333", flex: 1 }}>
              {titulo}
              {extra && (
                <span style={{ color: "#9a9a9a", fontWeight: "normal" }}> {extra}</span>
              )}
            </span>
            {est && (
              <S.HitoStatusBadge className={est === "OK" ? "completed" : "subsanacion"}>
                {est}
              </S.HitoStatusBadge>
            )}
          </div>
          {abierto && <div style={{ padding: "0 0.75rem 0.5rem" }}>{tablaFilas(filasPanel)}</div>}
        </div>
      );
    };

    const renderGrupo = (grupo, titulo, tituloPanel) => {
      const paneles = panelesDelGrupo(grupo);
      if (paneles.length === 0) return null;
      return (
        <div
          style={{
            border: "1px solid #e3e3e3",
            borderRadius: 6,
            marginBottom: "0.75rem",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "0.5rem 0.75rem",
              background: "#f7f7f8",
              fontWeight: "bold",
              fontSize: "0.85rem",
              color: "#333",
            }}
          >
            {titulo}{" "}
            <span style={{ color: "#9a9a9a", fontWeight: "normal" }}>({paneles.length})</span>
          </div>
          {paneles.map((p, i) => renderPanel(p.clave, tituloPanel(p, i), p.filas))}
        </div>
      );
    };

    const panelesReserva = panelesDelGrupo("Reserva");
    return (
      <div style={{ border: "1px dashed #d81b60", borderRadius: 6, padding: "0.75rem", marginTop: "1rem" }}>
        <p style={{ color: "#d81b60", fontWeight: "bold", marginBottom: "0.5rem" }}>
          ● Contraste IA / E2E
        </p>
        {renderGrupo("Interviniente", "Intervinientes compradores", (p, i) =>
          p.nombre || `Comprador ${i + 1}`
        )}
        {renderGrupo("Activo", "Activos", (p, i) =>
          `Activo ${i + 1}${p.nombre ? " — " + p.nombre : ""}`
        )}
        {panelesReserva.length > 0 && (
          <div
            style={{
              border: "1px solid #e3e3e3",
              borderRadius: 6,
              marginBottom: "0.75rem",
              overflow: "hidden",
            }}
          >
            {panelesReserva.map((p) =>
              renderPanel(
                p.clave,
                idTipo === TIPO_HITO.ESCRITURA ? "Datos de escritura" : "Datos del contrato privado",
                p.filas,
                `${p.filas.length} campos`
              )
            )}
          </div>
        )}
        {/* DNE2E-131 (rediseño): "Datos de subsanación" solo cuando hay algún campo con
            discrepancia (KO). El "Requiere subsanación" ya no es a nivel de hito (es por campo,
            en la tabla de arriba). Se deben rellenar estos datos al marcar campos "Subsanado". */}
        {hayKo && (
          <div
            style={{
              marginTop: "0.75rem",
              padding: "0.85rem",
              borderRadius: 6,
              border: "1px solid #cfe6d3",
              borderLeft: "4px solid #43a047",
              background: "#f2fbf4",
            }}
          >
            <p style={{ fontWeight: "bold", color: "#2f7d47", margin: "0 0 0.6rem", fontSize: "0.85rem" }}>
              ✎ Datos de subsanación
            </p>
            <S.Row columns="1fr 1fr">
              <InputList
                field={{ label: "Estado subsanación", name: "estadoSubsanacion", list: "estadoSubs" }}
                value={hito.estadoSubsanacion || ""}
                options={OPCIONES_ESTADO_SUBSANACION.map((o) => ({ value: o, label: o }))}
                handleChange={(field, value) => handleHitoChange(idTipo, "estadoSubsanacion", value)}
                isClearable={true}
                disabled={readOnly}
              />
              <S.FieldGroup>
                <p>Fecha subsanación completada</p>
                <p className="value">{hito.fechaSubsanada ? hito.fechaSubsanada.replace("T", " ") : "-"}</p>
              </S.FieldGroup>
            </S.Row>
            <S.Row columns="1fr 1fr">
              {/* Motivo automático (AF §4.1.4): lo calcula el sistema según los campos KO. */}
              <S.FieldGroup>
                <p>Motivo de subsanación</p>
                <p className="value">{hito.motivoSubsanacion || "-"}</p>
              </S.FieldGroup>
              <InputText
                field={{ label: "Responsable subsanación", name: "responsableSubsanacion" }}
                value={hito.responsableSubsanacion || ""}
                handleChange={(name, value) => handleHitoChange(idTipo, name, value)}
                disabled={readOnly}
              />
            </S.Row>
            <S.Row columns="1fr">
              <InputTextArea
                field={{ label: "Observaciones subsanación", name: "observacionesSubsanacion" }}
                rows={2}
                value={hito.observacionesSubsanacion || ""}
                handleChange={(name, value) => handleHitoChange(idTipo, name, value)}
                disabled={readOnly}
              />
            </S.Row>
          </div>
        )}
      </div>
    );
  };

  // DNE2E: documentos de un hito. Se prioriza el vínculo persistido (tabla pivote
  // PostFirmaHitoDocumento); para documentos sin vínculo (legado) se cae al filtro
  // por tipo de documento (lista por código).
  const docsDeHito = (idHito, tipos) =>
    (documentosReserva || []).filter((d) => {
      const links = d.idsPostFirmaHito || [];
      if (links.length > 0) return idHito != null && links.includes(idHito);
      return (tipos || []).includes(d.idTipoDocumento);
    });

  // Lista de tipos de documento restringida a los tipos del hito (para el desplegable del modal)
  const listsParaHito = (tipos) => {
    const base = lists?.TipoDocumentoReserva;
    if (!base) return lists;
    return {
      ...lists,
      TipoDocumentoReserva: {
        ...base,
        valores: (base.valores || []).filter((v) => tipos.includes(Number(v.valor))),
      },
    };
  };

  const addDocumentoHito = (data) => {
    // Resolver el hito destino según el modal abierto, antes de cerrarlo.
    const tipoHitoDestino =
      docModal === "escritura"
        ? TIPO_HITO.ESCRITURA
        : docModal === "contratoPrivado"
        ? TIPO_HITO.CONTRATO_PRIVADO
        : null;
    setDocModal(null);
    data.idTarea = idTarea;
    // DNE2E: vincular el documento al hito (tabla pivote) si el hito ya existe.
    const hitoDestino =
      tipoHitoDestino != null ? getHitoByTipo(hitosState, tipoHitoDestino) : null;
    if (hitoDestino && hitoDestino.id) data.idPostFirmaHito = hitoDestino.id;
    API.crearDocumentoReserva(data)
      .then(() => {
        toast.success("Documento creado");
        if (onHitoSaved) onHitoSaved();
      })
      .catch((error) => {
        toast.error("No se pudo crear el documento\n" + (error?.msg || ""));
      });
  };

  const confirmarEliminarDoc = () => {
    if (!docDelete) return;
    const idDocumento = docDelete.id;
    setDocDelete(null);
    API.eliminarDocumentoReserva({ idDocumento })
      .then(() => {
        toast.success("Documento eliminado");
        if (onHitoSaved) onHitoSaved();
      })
      .catch((error) => {
        toast.error("No se pudo eliminar el documento\n" + (error?.msg || ""));
      });
  };

  const guardarHito = async (idTipo, completar = false) => {
    const hito = getHitoByTipo(hitosState, idTipo);
    if (!hito || !hito.id) {
      toast.error("No se puede guardar un hito sin datos del backend");
      return;
    }

    // Validaciones generales (aplican al guardar y al completar)
    // 1. Fechas no pueden ser mayores al día actual
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    const hoyStr = new Date().toISOString().substring(0, 10);
    if (hito.fecComunicacionFinanzas && hito.fecComunicacionFinanzas > hoyStr) {
      toast.error("La fecha de comunicación a finanzas no puede ser mayor a la fecha actual");
      return;
    }
    if (hito.fecSubidaDocPrinex && hito.fecSubidaDocPrinex > hoyStr) {
      toast.error("La fecha de subida de documento a Prinex no puede ser mayor a la fecha actual");
      return;
    }
    if (hito.fechaSubsanada && hito.fechaSubsanada.substring(0, 10) > hoyStr) {
      toast.error("La fecha subsanada no puede ser mayor a la fecha actual");
      return;
    }
    // Estado subsanación = "Resuelto" solo si ningún campo queda en "Requiere subsanación = Sí".
    // El backend lo valida igual; esto evita el viaje al servidor.
    if (
      (idTipo === TIPO_HITO.ESCRITURA || idTipo === TIPO_HITO.CONTRATO_PRIVADO) &&
      (hito.estadoSubsanacion || "").trim().toLowerCase() === "resuelto" &&
      hitoRequiereSubsanacion(hito)
    ) {
      toast.error(
        "No se puede guardar el estado de subsanación como 'Resuelto' mientras haya campos con " +
          "'Requiere subsanación = Sí'. Cada campo debe quedar en 'No' o 'Subsanado'."
      );
      return;
    }
    // Validar fechas de adendas
    if (idTipo === TIPO_HITO.CONTRATO_PRIVADO && hito.adendas) {
      for (const adenda of hito.adendas) {
        if (adenda.fecComunicacionFinanzas && adenda.fecComunicacionFinanzas > hoyStr) {
          toast.error(`Adenda ${adenda.numeroAdenda}: La fecha de comunicación a finanzas no puede ser mayor a la fecha actual`);
          return;
        }
        if (adenda.fecSubidaDocPrinex && adenda.fecSubidaDocPrinex > hoyStr) {
          toast.error(`Adenda ${adenda.numeroAdenda}: La fecha de subida doc. Prinex no puede ser mayor a la fecha actual`);
          return;
        }
      }
    }

    // 2. Importes no pueden ser mayores al importe total (solo hito Escritura)
    if (idTipo === TIPO_HITO.ESCRITURA) {
      const impTotalRaw = datosGenerales?.importeTotal;
      const impTotal = impTotalRaw != null ? Number(impTotalRaw) : null;
      if (impTotal != null && !isNaN(impTotal)) {
        const impCheque = hito.importeChequeTransferencia != null && hito.importeChequeTransferencia !== ""
          ? Number(hito.importeChequeTransferencia) : null;
        const impAplazado = hito.importeCVAplazadoEnEscritura != null && hito.importeCVAplazadoEnEscritura !== ""
          ? Number(hito.importeCVAplazadoEnEscritura) : null;
        if (impCheque != null && impCheque > impTotal) {
          toast.error(`El importe cheque/transferencia no puede ser mayor al importe total (${formatImporte(impTotal)})`);
          return;
        }
        if (impAplazado != null && impAplazado > impTotal) {
          toast.error(`El importe CV aplazado no puede ser mayor al importe total (${formatImporte(impTotal)})`);
          return;
        }
      }
    }

    // Validación de orden: solo para completar hitos obligatorios
    if (completar && !puedeCompletar(idTipo, hitosState)) {
      toast.error(
        "No se puede completar este hito sin haber completado el hito obligatorio anterior"
      );
      return;
    }

    // Validación de campos: solo para completar
    if (completar) {
      const errores = validarCamposParaCompletar(idTipo, hito);
      if (errores.length > 0) {
        errores.forEach((err) => toast.error(err));
        return;
      }
    }

    setSaving(idTipo);
    try {
      const data = {
        id: hito.id,
        idTarea: idTarea,
        fecComunicacionFinanzas: toISOForBackend(hito.fecComunicacionFinanzas),
        fecSubidaDocPrinex: toISOForBackend(hito.fecSubidaDocPrinex),
        observaciones: hito.observaciones || "",
        completar: completar,
      };

      if (idTipo === TIPO_HITO.ESCRITURA) {
        data.dobleFiscalidad = hito.dobleFiscalidad != null && hito.dobleFiscalidad !== ""
          ? hito.dobleFiscalidad === "1" || hito.dobleFiscalidad === true
          : null;
        data.comunidadDeBienes = hito.comunidadDeBienes != null && hito.comunidadDeBienes !== ""
          ? hito.comunidadDeBienes === "1" || hito.comunidadDeBienes === true
          : null;
        data.importeCVAplazadoEnEscritura =
          hito.importeCVAplazadoEnEscritura != null && hito.importeCVAplazadoEnEscritura !== ""
            ? Number(hito.importeCVAplazadoEnEscritura)
            : null;
        // Fecha reserva/CP (manual): se persiste en la reserva vía el guardado del hito Escritura.
        data.fechaReservaCpEscritura = toISOForBackend(hito.fechaReservaCpEscritura);
        data.importeChequeTransferencia =
          hito.importeChequeTransferencia != null && hito.importeChequeTransferencia !== ""
            ? Number(hito.importeChequeTransferencia)
            : null;
        // DNE2E-131 (correos): desglose de cheque/transferencia hasta 6 importes
        [
          "importeChequeTransferencia2",
          "importeChequeTransferencia3",
          "importeChequeTransferencia4",
          "importeChequeTransferencia5",
          "importeChequeTransferencia6",
        ].forEach((f) => {
          data[f] = hito[f] != null && hito[f] !== "" ? Number(hito[f]) : null;
        });
        // DNE2E-131 (T1.3): Clausula CCPP y Clausula IBI (catalogos existentes)
        data.clausulaCCPP = hito.clausulaCCPP || "";
        data.clausulaIBI = hito.clausulaIBI || "";
        // DNE2E-131 (correos): Venta aplazada (manual E2E para el contraste IA)
        data.ventaAplazada =
          hito.ventaAplazada != null && hito.ventaAplazada !== ""
            ? hito.ventaAplazada === "1" || hito.ventaAplazada === true
            : null;
      }

      if (idTipo === TIPO_HITO.CONTRATO_PRIVADO) {
        // Fecha vencimiento: manual en E2E para el contraste con la IA.
        // "Nº pagos" ya no se manda: lo deduce el backend de los pagos del hito y enviarlo solo
        // serviría para pisar ese cálculo con un valor tecleado (negocio, 01-oct-2026).
        data.fechaVencimiento = toISOForBackend(hito.fechaVencimiento);
      }

      if (idTipo === TIPO_HITO.ANULACION_SUBSANACION) {
        data.subsanar = hito.subsanar || "";
        data.fechaSubsanada = toISOForBackend(hito.fechaSubsanada);
        data.tributacionEscritura = hito.tributacionEscritura || "";
        data.financiacion = hito.financiacion || "";
        data.retencionProvisionFondos = hito.retencionProvisionFondos || "";
        data.clausulaIBI = hito.clausulaIBI || "";
        data.clausulaCCPP = hito.clausulaCCPP || "";
        data.gestoriaTramiteCancelacion =
          hito.gestoriaTramiteCancelacion || "";
      }

      if (idTipo === TIPO_HITO.CONTRATO_PRIVADO && hito.adendas) {
        data.adendas = hito.adendas.map((a) => ({
          id: a.id,
          fecComunicacionFinanzas: toISOForBackend(a.fecComunicacionFinanzas),
          fecSubidaDocPrinex: toISOForBackend(a.fecSubidaDocPrinex),
          completar: a.completado || false,
        }));
      }

      // DNE2E-131 (rediseño): subsanación integrada (Escritura y Contrato Privado)
      if (idTipo === TIPO_HITO.ESCRITURA || idTipo === TIPO_HITO.CONTRATO_PRIVADO) {
        data.motivoSubsanacion = hito.motivoSubsanacion || "";
        data.estadoSubsanacion = hito.estadoSubsanacion || "";
        data.responsableSubsanacion = hito.responsableSubsanacion || "";
        data.observacionesSubsanacion = hito.observacionesSubsanacion || "";
        // DNE2E-131 (rediseño): "Requiere Subsanación" por campo (Sí/No/Subsanado). OK -> "No".
        data.subsanaciones = (hito.subsanaciones || []).map((s) => ({
          id: s.id,
          valorIa: s.valorIa || "",
          valorE2E: s.valorE2E || "",
          estado: s.estado || "",
          requiereSubsanacion:
            s.estado === "KO" ? (s.requiereSubsanacion || "Sí") : s.estado ? "No" : "",
        }));
      }

      const res = await API.guardarCompletarHitoPostFirma(data);
      if (res.resultadoPeticion === 1) {
        toast.success("Hito guardado con éxito");
        // Si estamos en modo subsanación y guardamos escritura, bloquear de nuevo
        // y marcar subsanar=Sí y fechaSubsanada=ahora en el hito Anulación
        if (idTipo === TIPO_HITO.ESCRITURA && subsanacionMode === "modificar" && !completar) {
          setSubsanacionMode(null);
          // Actualizar el hito Anulación con subsanar=Sí y fechaSubsanada=ahora
          const hitoAnul = getHitoByTipo(hitosState, TIPO_HITO.ANULACION_SUBSANACION);
          if (hitoAnul && hitoAnul.id) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            const fechaAhora = now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate()) +
              "T" + pad(now.getHours()) + ":" + pad(now.getMinutes());
            // Actualizar estado local
            setHitosState((prev) =>
              prev.map((h) =>
                h.idTipo === TIPO_HITO.ANULACION_SUBSANACION
                  ? { ...h, subsanar: "1", fechaSubsanada: fechaAhora }
                  : h
              )
            );
            // Guardar en backend
            try {
              await API.guardarCompletarHitoPostFirma({
                id: hitoAnul.id,
                idTarea: idTarea,
                fecComunicacionFinanzas: toISOForBackend(hitoAnul.fecComunicacionFinanzas),
                fecSubidaDocPrinex: toISOForBackend(hitoAnul.fecSubidaDocPrinex),
                observaciones: hitoAnul.observaciones || "",
                completar: false,
                subsanar: "1",
                fechaSubsanada: fechaAhora,
                tributacionEscritura: hitoAnul.tributacionEscritura || "",
                financiacion: hitoAnul.financiacion || "",
                retencionProvisionFondos: hitoAnul.retencionProvisionFondos || "",
                clausulaIBI: hitoAnul.clausulaIBI || "",
                clausulaCCPP: hitoAnul.clausulaCCPP || "",
                gestoriaTramiteCancelacion: hitoAnul.gestoriaTramiteCancelacion || "",
              });
            } catch (e) {
              // No bloquear el flujo si falla el guardado de anulación
              console.error("Error al actualizar hito Anulación tras subsanación:", e);
            }
          }
        }
        if (onHitoSaved) onHitoSaved();
      } else {
        toast.error(res.mensajeUsuario || "Error al guardar el hito");
      }
    } catch (error) {
      const msg = error?.msg || "Error al guardar el hito";
      toast.error(msg);
    } finally {
      setSaving(null);
    }
  };

  const disabled = readOnly || !access?.Editar;

  // Helper: etiqueta con asterisco rojo para campos obligatorios
  const requiredLabel = (label) => (
    <>{label} <span style={{ color: "red", fontWeight: "bold" }}>*</span></>
  );

  // DNE2E-131 (rediseño): ¿el hito requiere subsanación? Ahora se deriva de los campos:
  // hay algún campo KO todavía sin resolver (Requiere Subsanación distinto de "No"/"Subsanado").
  const hitoRequiereSubsanacion = (hitoReal) =>
    !!hitoReal &&
    (hitoReal.subsanaciones || []).some(
      (f) =>
        f.estado === "KO" &&
        f.requiereSubsanacion !== "No" &&
        f.requiereSubsanacion !== "Subsanado"
    );

  // Helper: clase CSS del SectionBox según estado del hito
  const getSectionClass = (hitoReal) => {
    if (!hitoReal) return "empty-section";
    if (hitoReal.completado) return "completed-section";
    if (hitoRequiereSubsanacion(hitoReal)) return "subsanacion-section";
    if (hitoReal.idTipo === activeHitoTipo) return "active-section";
    return "";
  };

  // Helper: badge de estado del hito (mockup: Completado / Requiere Subsanación /
  // No aplica / Bloqueado / Activo / Pendiente)
  const renderHitoStatusBadge = (idTipo, hitoReal) => {
    if (!hitoReal) {
      return <S.HitoStatusBadge className="noaplica">No aplica</S.HitoStatusBadge>;
    }
    if (hitoReal.completado) {
      return <S.HitoStatusBadge className="completed">Completado</S.HitoStatusBadge>;
    }
    if (hitoRequiereSubsanacion(hitoReal)) {
      return <S.HitoStatusBadge className="subsanacion">Requiere Subsanación</S.HitoStatusBadge>;
    }
    // DNE2E-131 (T1.4): la Escritura no se muestra "Activo" mientras no esté completada la tarea
    // Confirmación de Firma, aunque los hitos previos ya lo estén. La condición del Reporte de
    // Gestoría se retiró a petición de negocio (24-sep-2026).
    const escrituraBloqueada =
      idTipo === TIPO_HITO.ESCRITURA &&
      subsanacionMode !== "modificar" &&
      !datosGenerales?.confirmacionFirmaCompletada;
    if (escrituraBloqueada) {
      return <S.HitoStatusBadge className="bloqueado">Bloqueado</S.HitoStatusBadge>;
    }
    if (
      activeHitoTipo === idTipo || (idTipo === TIPO_HITO.ESCRITURA && subsanacionMode === "modificar")
    ) {
      return <S.HitoStatusBadge className="active">Activo</S.HitoStatusBadge>;
    }
    return <S.HitoStatusBadge className="pending">Pendiente</S.HitoStatusBadge>;
  };

  const hitoReservaReal = getHitoByTipo(hitosState, TIPO_HITO.RESERVA);
  const hitoRescisionReservaReal = getHitoByTipo(
    hitosState,
    TIPO_HITO.RESCISION_RESERVA
  );
  const hitoRescisionContratoReal = getHitoByTipo(
    hitosState,
    TIPO_HITO.RESCISION_CONTRATO_PRIVADO
  );
  const hitoContratoPrivadoReal = getHitoByTipo(
    hitosState,
    TIPO_HITO.CONTRATO_PRIVADO
  );
  const hitoEscrituraReal = getHitoByTipo(hitosState, TIPO_HITO.ESCRITURA);
  const hitoAnulacionReal = getHitoByTipo(
    hitosState,
    TIPO_HITO.ANULACION_SUBSANACION
  );

  const hitoReserva = hitoReservaReal || EMPTY_HITO;
  const hitoRescisionReserva = hitoRescisionReservaReal || EMPTY_HITO;
  const hitoRescisionContrato = hitoRescisionContratoReal || EMPTY_HITO;
  const hitoContratoPrivado = hitoContratoPrivadoReal || EMPTY_HITO;
  const hitoEscritura = hitoEscrituraReal || {
    ...EMPTY_HITO,
    dobleFiscalidad: null,
    comunidadDeBienes: null,
    importeCVAplazadoEnEscritura: null,
    fechaReservaCpEscritura: null,
    importeChequeTransferencia: null,
    clausulaCCPP: "",
    clausulaIBI: "",
  };
  const hitoAnulacion = hitoAnulacionReal || {
    ...EMPTY_HITO,
    subsanar: "",
    fechaSubsanada: "",
    tributacionEscritura: "",
    financiacion: "",
    retencionProvisionFondos: "",
    clausulaIBI: "",
    clausulaCCPP: "",
    gestoriaTramiteCancelacion: "",
  };

  // Determinar qué hito está activo (menor idTipo, existe, no completado)
  // Anulación/Subsanación Escritura no se activa hasta que Escritura esté completada
  const activeHitoTipo = (() => {
    const noCompletados = hitosState
      .filter((h) => h && h.id && !h.completado)
      .filter(
        (h) =>
          h.idTipo !== TIPO_HITO.ANULACION_SUBSANACION ||
          (hitoEscrituraReal && hitoEscrituraReal.completado)
      )
      .sort((a, b) => a.idTipo - b.idTipo);
    return noCompletados.length > 0 ? noCompletados[0].idTipo : null;
  })();

  // Un hito es editable solo si es el activo (o escritura en modo subsanación)
  const isHitoEditable = (idTipo, hitoReal) => {
    if (disabled || !hitoReal) return false;
    // Los hitos Reserva y Contrato Privado solo se crean con su importe > 0. Si el CRM lo pone a
    // 0 después, el hito sigue existiendo (no se borra, puede tener datos y subsanación) pero deja
    // de poder editarse: ya no aplica (negocio, 01-oct-2026).
    if (idTipo === TIPO_HITO.RESERVA && datosGenerales?.importeReservaACero) return false;
    if (
      idTipo === TIPO_HITO.CONTRATO_PRIVADO &&
      datosGenerales?.importeContratoPrivadoACero
    )
      return false;
    if (idTipo === TIPO_HITO.ESCRITURA && subsanacionMode === "modificar") return true;
    // DNE2E-131 (T1.4): el hito Escritura solo se habilita cuando está Completada la tarea
    // Confirmación de Firma (además de los hitos obligatorios previos). La condición del Reporte
    // de Gestoría se retiró a petición de negocio (24-sep-2026).
    if (idTipo === TIPO_HITO.ESCRITURA && !datosGenerales?.confirmacionFirmaCompletada)
      return false;
    return activeHitoTipo === idTipo;
  };

  const disabledReserva = !isHitoEditable(TIPO_HITO.RESERVA, hitoReservaReal);
  const disabledRescisionReserva = !isHitoEditable(TIPO_HITO.RESCISION_RESERVA, hitoRescisionReservaReal);
  const disabledRescisionContrato = !isHitoEditable(TIPO_HITO.RESCISION_CONTRATO_PRIVADO, hitoRescisionContratoReal);
  const disabledContratoPrivado = !isHitoEditable(TIPO_HITO.CONTRATO_PRIVADO, hitoContratoPrivadoReal);
  const disabledEscritura = !isHitoEditable(TIPO_HITO.ESCRITURA, hitoEscrituraReal);
  const disabledAnulacion = !isHitoEditable(TIPO_HITO.ANULACION_SUBSANACION, hitoAnulacionReal);
  const escrituraCompletada = hitoEscrituraReal && hitoEscritura.completado;

  const importeReserva = formatImporte(datosGenerales?.importeReserva);
  const importeContratoPrivado = formatImporte(
    datosGenerales?.impContratoPrivado
  );
  const importeTotal = formatImporte(datosGenerales?.importeTotal);

  const guardarTodosLosHitos = async () => {
    // Solo guardar el hito activo (el que se está editando)
    const idTipoActivo = subsanacionMode === "modificar"
      ? TIPO_HITO.ESCRITURA
      : activeHitoTipo;

    if (idTipoActivo == null) {
      toast.error("No hay un hito activo para guardar");
      return;
    }

    await guardarHito(idTipoActivo, false);
  };

  // Exponer la función de guardado en lote al padre via ref
  useEffect(() => {
    if (guardarTodosRef) {
      guardarTodosRef.current = guardarTodosLosHitos;
    }
  });


  // DNE2E-131: ¿el hito ya tiene lectura de la IA? (Escritura y Contrato Privado)
  const tieneLecturaIa = (hito) =>
    !!hito &&
    (hito.iaProcesada === true ||
      (hito.subsanaciones || []).some((f) => f.valorIa != null && f.valorIa !== ""));

  // DNE2E-131 (AF 4.1.2): ¿el hito lo auto-completó el proceso de contraste con IA?
  const autocompletadoIa = (hito) =>
    !!hito && hito.completado && hito.usuarioCompletado === "Proceso Contraste IA";

  // DNE2E-131 (negocio, 06-oct-2026): cuando NO hay lectura de la IA, la comparativa se pintaba
  // con la columna "Valor IA" vacía y sin ninguna explicación. Esa pantalla es idéntica a la de
  // "la IA leyó el documento y no encontró esos datos", así que el usuario no podía distinguir
  // un caso del otro. Se distinguen los dos motivos posibles:
  //   · falta por subir el documento del hito -> la IA no tiene qué leer;
  //   · el documento está subido pero la IA aún no lo ha procesado -> se reintenta solo.
  const renderSinLecturaIa = (hito, idTipo) => {
    if (idTipo !== TIPO_HITO.CONTRATO_PRIVADO && idTipo !== TIPO_HITO.ESCRITURA) return null;
    if (!hito || !hito.id) return null;
    if (tieneLecturaIa(hito)) return null;   // sí hay lectura: lo cuenta renderResultadoIa

    const esCp = idTipo === TIPO_HITO.CONTRATO_PRIVADO;
    const tipos = esCp ? DOC_TIPOS_CONTRATO_PRIVADO : DOC_TIPOS_ESCRITURA;
    const subidos = docsDeHito(hito.id, tipos);
    // Los tipos que faltan por subir. En Escritura son dos (copia simple y cheque) y hacen
    // falta los dos: el contraste no pregunta a la IA hasta tenerlos todos.
    const faltan = tipos.filter(
      (tipo) => !subidos.some((d) => d.idTipoDocumento === tipo)
    );
    const nombresQueFaltan = faltan
      .map((tipo) => DOC_TIPO_LABELS[tipo] || `documento ${tipo}`)
      .join(" y ");

    const mensaje = faltan.length
      ? `Pendiente de subir ${nombresQueFaltan}: hasta que no esté, la IA no puede leerlo y el contraste se queda sin datos.`
      : esCp
      ? "El contrato privado aún no lo ha leído la IA. El contraste se volverá a ejecutar automáticamente."
      : "Los documentos aún no los ha leído la IA. El contraste se volverá a ejecutar automáticamente.";

    return (
      <p style={{ color: "#b26a00", margin: "8px 0", fontWeight: "bold", fontSize: "0.85rem" }}>
        {mensaje}
      </p>
    );
  };

  // DNE2E-131 (AF 4.1.2): resultado del contraste IA (badge "Todos OK / Con discrepancias"
  // + resumen de subsanación) para los hitos con IA (Contrato Privado y Escritura).
  const renderResultadoIa = (hito, idTipo) => {
    if (idTipo !== TIPO_HITO.CONTRATO_PRIVADO && idTipo !== TIPO_HITO.ESCRITURA) return null;
    if (!tieneLecturaIa(hito)) return null;
    const hayKo = (hito.subsanaciones || []).some((f) => f.estado === "KO");
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          margin: "0.5rem 0 0.75rem",
        }}
      >
        <span style={{ fontWeight: "bold", fontSize: "0.85rem", color: "#555" }}>
          Resultado Validación IA:
        </span>
        <S.HitoStatusBadge className={hayKo ? "subsanacion" : "completed"}>
          {hayKo ? "Con discrepancias" : "Todos OK"}
        </S.HitoStatusBadge>
        <span
          style={{
            fontSize: "0.82rem",
            fontWeight: "bold",
            color: hayKo ? "#b0316a" : "#2f7d47",
          }}
        >
          {hayKo
            ? "Subsanación: Requerida - Hay campos con discrepancia"
            : "Subsanación: No requerida - Todos los campos OK"}
        </span>
      </div>
    );
  };

  const renderBotonesHito = (idTipo, disabledSection, hitoReal) => {
    if (!hitoReal) return null;
    const isSaving = saving === idTipo;
    const canComplete =
      !disabledSection &&
      !hitoReal.completado &&
      puedeCompletar(idTipo, hitosState);
    return (
      <S.HitoButtons style={{ display: "flex", justifyContent: "flex-end" }}>
        {/* DNE2E-131: botón del hito alineado a la derecha. El guardado se hace con
            el botón "Guardar" de la tarea (parte inferior), no por hito. */}
        {!hitoReal.completado && (
          <Button
            label={isSaving ? "Completando..." : "Completar Hito"}
            onClick={() => {
              // Validar antes de mostrar el modal
              if (!puedeCompletar(idTipo, hitosState)) {
                toast.error("No se puede completar este hito sin haber completado el hito obligatorio anterior");
                return;
              }
              const hito = getHitoByTipo(hitosState, idTipo);
              // DNE2E-131: los hitos con IA (Contrato Privado y Escritura) no se pueden
              // completar sin lectura de la IA. Se avisa antes de abrir el modal.
              if (
                (idTipo === TIPO_HITO.CONTRATO_PRIVADO ||
                  idTipo === TIPO_HITO.ESCRITURA) &&
                !tieneLecturaIa(hito)
              ) {
                toast.error(
                  "No se puede completar el hito hasta que la IA haya realizado la lectura de los documentos."
                );
                return;
              }
              // DNE2E-131 (rediseño): con discrepancias (KO), para completar cada campo debe
              // quedar en "No" o "Subsanado" (ninguno en "Sí"). Y si hay algún "Subsanado",
              // deben rellenarse motivo y responsable de la subsanación.
              if (
                (idTipo === TIPO_HITO.CONTRATO_PRIVADO ||
                  idTipo === TIPO_HITO.ESCRITURA) &&
                hito
              ) {
                const filasKo = (hito.subsanaciones || []).filter((f) => f.estado === "KO");
                const algunSi = filasKo.some(
                  (f) => f.requiereSubsanacion !== "No" && f.requiereSubsanacion !== "Subsanado"
                );
                const algunSubsanado = filasKo.some(
                  (f) => f.requiereSubsanacion === "Subsanado"
                );
                if (algunSi) {
                  toast.error(
                    "Hay campos con discrepancia marcados como 'Requiere subsanación = Sí'. Deben quedar en 'No' o 'Subsanado' para completar."
                  );
                  return;
                }
                if (
                  algunSubsanado &&
                  (!hito.motivoSubsanacion || !hito.responsableSubsanacion)
                ) {
                  toast.error(
                    "Para marcar campos como 'Subsanado' indica el motivo y el responsable de la subsanación."
                  );
                  return;
                }
              }
              if (hito) {
                const errores = validarCamposParaCompletar(idTipo, hito);
                if (errores.length > 0) {
                  errores.forEach((err) => toast.error(err));
                  return;
                }
              }
              setConfirmCompletarHito(idTipo);
            }}
            disabled={!canComplete || isSaving}
          />
        )}
      </S.HitoButtons>
    );
  };

  // DNE2E-131 (rediseño): stepper "Progreso de Hitos" (5 pasos como el mockup).
  // Rescisión (paso 3) agrupa los tipos 3 y 4.
  const PASOS_HITOS = [
    { n: 1, label: "Reserva", tipos: [TIPO_HITO.RESERVA] },
    { n: 2, label: "C. Privado", tipos: [TIPO_HITO.CONTRATO_PRIVADO] },
    { n: 3, label: "Rescisión", tipos: [TIPO_HITO.RESCISION_RESERVA, TIPO_HITO.RESCISION_CONTRATO_PRIVADO] },
    { n: 4, label: "Escritura", tipos: [TIPO_HITO.ESCRITURA] },
    { n: 5, label: "Anul./Subsan.", tipos: [TIPO_HITO.ANULACION_SUBSANACION] },
  ];

  const renderStepper = () => (
    <S.Stepper>
      {PASOS_HITOS.map((p) => {
        const hito = p.tipos
          .map((t) => getHitoByTipo(hitosState, t))
          .find(Boolean);
        const clase = hito && hito.completado ? "done" : hito ? "active" : "pending";
        return (
          <S.Step key={p.n} className={clase}>
            <S.StepCircle className={clase}>{p.n}</S.StepCircle>
            <S.StepLabel>{p.label}</S.StepLabel>
          </S.Step>
        );
      })}
    </S.Stepper>
  );

  return (
    <S.Wrapper>
      <S.Header>
        <p>Datos Post-firma - Progreso de Hitos</p>
      </S.Header>
      <S.Content>
        {renderStepper()}
        {/* ---- Reserva (Obligatorio) ---- */}
        <S.SectionBox className={getSectionClass(hitoReservaReal)}>
          <S.SectionBoxHeader>
            <span>Reserva (Obligatorio)</span>
            {renderHitoStatusBadge(TIPO_HITO.RESERVA, hitoReservaReal)}
          </S.SectionBoxHeader>
          <S.FieldGroup>
            <p>Importe Reserva</p>
            <p className="value">{importeReserva}</p>
          </S.FieldGroup>
          <S.Row>
            <InputDate
              field={{
                label: requiredLabel("Fecha com. finanzas"),
                name: "fecComunicacionFinanzas",
                type: "date",
              }}
              value={hitoReserva.fecComunicacionFinanzas || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.RESERVA, name, value)
              }
              disabled={disabledReserva}
            />
            <InputDate
              field={{
                label: requiredLabel("Fecha subida doc. Prinex"),
                name: "fecSubidaDocPrinex",
                type: "date",
              }}
              value={hitoReserva.fecSubidaDocPrinex || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.RESERVA, name, value)
              }
              disabled={disabledReserva}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoReserva.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(TIPO_HITO.RESERVA, name, value)
            }
            disabled={disabledReserva}
          />
          {renderBotonesHito(TIPO_HITO.RESERVA, disabledReserva, hitoReservaReal)}
        </S.SectionBox>

        {/* ---- Contrato privado + Adendas (Obligatorio) ---- */}
        <S.SectionBox className={getSectionClass(hitoContratoPrivadoReal)}>
          <S.SectionBoxHeader>
            <span>Contrato privado (Obligatorio)</span>
            {autocompletadoIa(hitoContratoPrivadoReal) && (
              <span style={{ color: "#2196f3", fontSize: "0.8rem", fontWeight: "bold" }}>
                Completado automáticamente por IA
              </span>
            )}
            {renderHitoStatusBadge(TIPO_HITO.CONTRATO_PRIVADO, hitoContratoPrivadoReal)}
          </S.SectionBoxHeader>
          {renderResultadoIa(hitoContratoPrivadoReal, TIPO_HITO.CONTRATO_PRIVADO)}
          {renderSinLecturaIa(hitoContratoPrivadoReal, TIPO_HITO.CONTRATO_PRIVADO)}
          {!hitoContratoPrivadoReal && (
            <p className="empty-msg">No se requiere contrato privado para esta operación.</p>
          )}
          {hitoContratoPrivadoReal && datosGenerales?.importeContratoPrivadoACero && (
            <p style={{ color: "#b26a00", margin: "8px 0", fontWeight: "bold" }}>
              El importe del contrato privado se ha actualizado a 0: este hito ya no aplica y no se
              puede modificar. Se mantiene a la vista con los datos que tuviera.
            </p>
          )}
          <S.HitoTable>
            <thead>
              <tr>
                <th>Hito</th>
                <th>Importe</th>
                <th>Fecha com. finanzas <span style={{ color: 'red' }}>*</span></th>
                <th>Fecha subida doc. Prinex <span style={{ color: 'red' }}>*</span></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Contrato privado</td>
                <td>{importeContratoPrivado}</td>
                <td>
                  <input
                    type="date"
                    value={
                      hitoContratoPrivado.fecComunicacionFinanzas || ""
                    }
                    onChange={(e) =>
                      handleHitoChange(
                        TIPO_HITO.CONTRATO_PRIVADO,
                        "fecComunicacionFinanzas",
                        e.target.value
                      )
                    }
                    disabled={disabledContratoPrivado}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    value={hitoContratoPrivado.fecSubidaDocPrinex || ""}
                    onChange={(e) =>
                      handleHitoChange(
                        TIPO_HITO.CONTRATO_PRIVADO,
                        "fecSubidaDocPrinex",
                        e.target.value
                      )
                    }
                    disabled={disabledContratoPrivado}
                  />
                </td>
              </tr>
              {(hitoContratoPrivado.adendas || []).map((adenda) => (
                <tr key={adenda.id}>
                  <td>Adenda {adenda.numeroAdenda}</td>
                  <td>{formatImporte(adenda.monto)}</td>
                  <td>
                    <input
                      type="date"
                      value={adenda.fecComunicacionFinanzas || ""}
                      onChange={(e) =>
                        handleAdendaChange(
                          TIPO_HITO.CONTRATO_PRIVADO,
                          adenda.id,
                          "fecComunicacionFinanzas",
                          e.target.value
                        )
                      }
                      disabled={
                        disabledContratoPrivado ||
                        (adenda.monto == null || Number(adenda.monto) <= 0)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={adenda.fecSubidaDocPrinex || ""}
                      onChange={(e) =>
                        handleAdendaChange(
                          TIPO_HITO.CONTRATO_PRIVADO,
                          adenda.id,
                          "fecSubidaDocPrinex",
                          e.target.value
                        )
                      }
                      disabled={
                        disabledContratoPrivado ||
                        (adenda.monto == null || Number(adenda.monto) <= 0)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </S.HitoTable>
          {/* DNE2E-131: "Fecha vencimiento" se rellena a mano en E2E para el contraste con la IA
              (no viene del ETL). "Nº pagos" YA NO se teclea (negocio, 01-oct-2026): se deduce de
              los pagos del hito —el del contrato privado más una por cada adenda con importe— así
              que se quitó del formulario para que nadie escriba un número que el contraste ignora.
              El valor calculado se ve en la fila "Nº pagos" de la comparativa IA / E2E. */}
          <S.Row>
            <InputDate
              field={{ label: "Fecha vencimiento", name: "fechaVencimiento", type: "date" }}
              value={hitoContratoPrivado.fechaVencimiento || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.CONTRATO_PRIVADO, name, value)
              }
              disabled={disabledContratoPrivado}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoContratoPrivado.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(TIPO_HITO.CONTRATO_PRIVADO, name, value)
            }
            disabled={disabledContratoPrivado}
          />
          {/* DNE2E-131 (T3.2): documento del hito Contrato Privado. El listado esta siempre
              visible (para consultar lo subido, incluso tras completar el hito); solo el alta
              se habilita cuando el hito Contrato Privado es editable (hito activo). */}
          {renderDocsRequeridos(hitoContratoPrivadoReal, TIPO_HITO.CONTRATO_PRIVADO)}
          <ModuleTareaDocsReserva
            data={docsDeHito(hitoContratoPrivadoReal?.id, DOC_TIPOS_CONTRATO_PRIVADO)}
            metadata={METADATA_DOCS_RESERVA}
            title="Documentos"
            requestAdd={
              !disabledContratoPrivado
                ? () => setDocModal("contratoPrivado")
                : undefined
            }
            requestDelete={
              !disabledContratoPrivado ? (doc) => setDocDelete(doc) : undefined
            }
            refreshDocs={() => onHitoSaved && onHitoSaved()}
            setLoading={() => {}}
            access={access}
          />
          {renderSubsanacion(hitoContratoPrivadoReal, TIPO_HITO.CONTRATO_PRIVADO, disabledContratoPrivado)}
          {renderBotonesHito(TIPO_HITO.CONTRATO_PRIVADO, disabledContratoPrivado, hitoContratoPrivadoReal)}
        </S.SectionBox>

        {/* ---- Rescisión de reserva (Opcional) ---- */}
        <S.SectionBox className={getSectionClass(hitoRescisionReservaReal)}>
          <S.SectionBoxHeader>
            <span>Rescisión de reserva (Opcional)</span>
            {renderHitoStatusBadge(TIPO_HITO.RESCISION_RESERVA, hitoRescisionReservaReal)}
          </S.SectionBoxHeader>
          {!hitoRescisionReservaReal && (
            <p className="empty-msg">No se ha producido rescisión de reserva.</p>
          )}
          <S.FieldGroup>
            <p>Importe Reserva</p>
            <p className="value">{importeReserva}</p>
          </S.FieldGroup>
          <S.Row>
            <InputDate
              field={{
                label: requiredLabel("Fecha com. finanzas"),
                name: "fecComunicacionFinanzas",
                type: "date",
              }}
              value={hitoRescisionReserva.fecComunicacionFinanzas || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.RESCISION_RESERVA, name, value)
              }
              disabled={disabledRescisionReserva}
            />
            <InputDate
              field={{
                label: requiredLabel("Fecha subida doc. Prinex"),
                name: "fecSubidaDocPrinex",
                type: "date",
              }}
              value={hitoRescisionReserva.fecSubidaDocPrinex || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.RESCISION_RESERVA, name, value)
              }
              disabled={disabledRescisionReserva}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoRescisionReserva.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(TIPO_HITO.RESCISION_RESERVA, name, value)
            }
            disabled={disabledRescisionReserva}
          />
          {renderBotonesHito(TIPO_HITO.RESCISION_RESERVA, disabledRescisionReserva, hitoRescisionReservaReal)}
        </S.SectionBox>

        {/* ---- Rescisión de contrato privado (Opcional) ---- */}
        <S.SectionBox className={getSectionClass(hitoRescisionContratoReal)}>
                    <S.SectionBoxHeader>
                      <span>Rescisión de contrato privado (Opcional)</span>
                      {renderHitoStatusBadge(TIPO_HITO.RESCISION_CONTRATO_PRIVADO, hitoRescisionContratoReal)}
                    </S.SectionBoxHeader>
          {!hitoRescisionContratoReal && (
            <p className="empty-msg">No se ha producido rescisión de contrato privado.</p>
          )}
          <S.FieldGroup>
            <p>Importe C. privado</p>
            <p className="value">{importeContratoPrivado}</p>
          </S.FieldGroup>
          <S.Row>
            <InputDate
              field={{
                label: requiredLabel("Fecha com. finanzas"),
                name: "fecComunicacionFinanzas",
                type: "date",
              }}
              value={hitoRescisionContrato.fecComunicacionFinanzas || ""}
              handleChange={(name, value) =>
                handleHitoChange(
                  TIPO_HITO.RESCISION_CONTRATO_PRIVADO,
                  name,
                  value
                )
              }
              disabled={disabledRescisionContrato}
            />
            <InputDate
              field={{
                label: requiredLabel("Fecha subida doc. Prinex"),
                name: "fecSubidaDocPrinex",
                type: "date",
              }}
              value={hitoRescisionContrato.fecSubidaDocPrinex || ""}
              handleChange={(name, value) =>
                handleHitoChange(
                  TIPO_HITO.RESCISION_CONTRATO_PRIVADO,
                  name,
                  value
                )
              }
              disabled={disabledRescisionContrato}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoRescisionContrato.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(
                TIPO_HITO.RESCISION_CONTRATO_PRIVADO,
                name,
                value
              )
            }
            disabled={disabledRescisionContrato}
          />
          {renderBotonesHito(TIPO_HITO.RESCISION_CONTRATO_PRIVADO, disabledRescisionContrato, hitoRescisionContratoReal)}
        </S.SectionBox>

        {/* ---- Escritura (Obligatorio) ---- */}
        <S.SectionBox
          className={subsanacionMode === "modificar" ? "" : getSectionClass(hitoEscrituraReal)}
        >
          <S.SectionBoxHeader>
            <span>Escritura (Obligatorio)</span>
            {autocompletadoIa(hitoEscrituraReal) && (
              <span style={{ color: "#2196f3", fontSize: "0.8rem", fontWeight: "bold" }}>
                Completado automáticamente por IA
              </span>
            )}
            {renderHitoStatusBadge(TIPO_HITO.ESCRITURA, hitoEscrituraReal)}
          </S.SectionBoxHeader>
          {renderResultadoIa(hitoEscrituraReal, TIPO_HITO.ESCRITURA)}
          {renderSinLecturaIa(hitoEscrituraReal, TIPO_HITO.ESCRITURA)}
          {/* DNE2E-131 (T1.4): avisos mientras no estén completadas las tareas requeridas
              (Confirmación de Firma y Reporte de Gestoría) para habilitar el hito Escritura. */}
          {hitoReservaReal && datosGenerales?.importeReservaACero && (
            <p style={{ color: "#b26a00", margin: "8px 0", fontWeight: "bold" }}>
              El importe de reserva se ha actualizado a 0: este hito ya no aplica y no se puede
              modificar. Se mantiene a la vista con los datos que tuviera.
            </p>
          )}
          {activeHitoTipo === TIPO_HITO.ESCRITURA &&
            subsanacionMode !== "modificar" &&
            !datosGenerales?.confirmacionFirmaCompletada && (
              <p style={{ color: "#b26a00", margin: "8px 0", fontWeight: "bold" }}>
                Pendiente de completar la tarea "Confirmación de Firma" para habilitar este hito.
              </p>
            )}
          {subsanacionMode === "modificar" && (
            <S.ModeIndicator variant="modificar">
              Modo Subsanación: Modificando escritura
              <Button
                label="Cancelar subsanación"
                onClick={() => {
                  setSubsanacionMode(null);
                  // Restaurar los datos originales del hito escritura desde el prop
                  const hitoOriginal = hitos.find((h) => h.idTipo === TIPO_HITO.ESCRITURA);
                  if (hitoOriginal) {
                    setHitosState((prev) =>
                      prev.map((h) =>
                        h.idTipo === TIPO_HITO.ESCRITURA
                          ? {
                              ...hitoOriginal,
                              fecComunicacionFinanzas: toDateOnly(hitoOriginal.fecComunicacionFinanzas),
                              fecSubidaDocPrinex: toDateOnly(hitoOriginal.fecSubidaDocPrinex),
                              fecCompletado: toDatetimeLocal(hitoOriginal.fecCompletado),
                              fechaSubsanada: toDatetimeLocal(hitoOriginal.fechaSubsanada),
                            }
                          : h
                      )
                    );
                  }
                }}
                type="cancel"
              />
            </S.ModeIndicator>
          )}
          <S.Row columns="repeat(5, 1fr)">
            <S.FieldGroup>
              <p>Importe Total OP (informativo)</p>
              <p className="value">{importeTotal}</p>
            </S.FieldGroup>
            <InputDate
              field={{
                label: requiredLabel("Fecha com. finanzas"),
                name: "fecComunicacionFinanzas",
                type: "date",
              }}
              value={hitoEscritura.fecComunicacionFinanzas || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.ESCRITURA, name, value)
              }
              disabled={disabledEscritura}
            />
            <InputDate
              field={{
                label: requiredLabel("Fecha subida doc. Prinex"),
                name: "fecSubidaDocPrinex",
                type: "date",
              }}
              value={hitoEscritura.fecSubidaDocPrinex || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.ESCRITURA, name, value)
              }
              disabled={disabledEscritura}
            />
            <InputList
              field={{
                label: requiredLabel("Doble Fiscalidad"),
                name: "dobleFiscalidad",
                list: "dobleFiscalidad",
              }}
              value={
                hitoEscritura.dobleFiscalidad != null
                  ? String(
                      hitoEscritura.dobleFiscalidad === true ? "1" :
                      hitoEscritura.dobleFiscalidad === false ? "0" :
                      hitoEscritura.dobleFiscalidad
                    )
                  : ""
              }
              options={FAKE_OPTIONS.dobleFiscalidad}
              handleChange={(field, value) =>
                handleHitoListChange(TIPO_HITO.ESCRITURA, field, value)
              }
              isClearable={true}
              disabled={disabledEscritura}
            />
            <InputList
              field={{
                label: requiredLabel("Comunidad de bienes"),
                name: "comunidadDeBienes",
                list: "comunidadBienes",
              }}
              value={
                hitoEscritura.comunidadDeBienes != null
                  ? String(
                      hitoEscritura.comunidadDeBienes === true ? "1" :
                      hitoEscritura.comunidadDeBienes === false ? "0" :
                      hitoEscritura.comunidadDeBienes
                    )
                  : ""
              }
              options={FAKE_OPTIONS.comunidadBienes}
              handleChange={(field, value) =>
                handleHitoListChange(TIPO_HITO.ESCRITURA, field, value)
              }
              isClearable={true}
              disabled={disabledEscritura}
            />
            {/* DNE2E-131 (correos): Venta aplazada se rellena a mano en E2E para el contraste IA */}
            <InputList
              field={{
                label: "Venta aplazada",
                name: "ventaAplazada",
                list: "ventaAplazada",
              }}
              value={
                hitoEscritura.ventaAplazada != null
                  ? String(
                      hitoEscritura.ventaAplazada === true ? "1" :
                      hitoEscritura.ventaAplazada === false ? "0" :
                      hitoEscritura.ventaAplazada
                    )
                  : ""
              }
              options={FAKE_OPTIONS.ventaAplazada}
              handleChange={(field, value) =>
                handleHitoListChange(TIPO_HITO.ESCRITURA, field, value)
              }
              isClearable={true}
              disabled={disabledEscritura}
            />
            {/* Fecha reserva/CP: se captura a mano en el hito Escritura y se guarda en la reserva
                (Reserva.FechaReservaCpEscritura); es el Valor E2E del contraste con IA. */}
            <InputDate
              field={{ label: "Fecha reserva/CP", name: "fechaReservaCpEscritura", type: "date" }}
              value={hitoEscritura.fechaReservaCpEscritura || ""}
              handleChange={(name, value) =>
                handleHitoChange(TIPO_HITO.ESCRITURA, name, value)
              }
              disabled={disabledEscritura}
            />
          </S.Row>
          <S.Row columns="repeat(4, 1fr)">
            {disabledEscritura ? (
              <S.FieldGroup>
                <p>{requiredLabel("Importe CV aplazado")}</p>
                <p className="value">
                  {formatImporte(hitoEscritura.importeCVAplazadoEnEscritura)}
                </p>
              </S.FieldGroup>
            ) : (
              <InputNumber
                field={{
                  label: requiredLabel("Importe CV aplazado"),
                  name: "importeCVAplazadoEnEscritura",
                  step: "0.01",
                  min: "0",
                }}
                value={
                  hitoEscritura.importeCVAplazadoEnEscritura != null
                    ? String(hitoEscritura.importeCVAplazadoEnEscritura)
                    : ""
                }
                handleChange={(name, value) =>
                  handleHitoChange(TIPO_HITO.ESCRITURA, name, value)
                }
              />
            )}
            {/* DNE2E-131 (correos): desglose de cheque/transferencia hasta 6 importes.
                Se muestran los 6; el 1º siempre habilitado y cada siguiente se habilita cuando
                el anterior es > 0. Al poner 0/vacío se limpian e inhabilitan los siguientes.
                El Valor E2E de contraste ("Importe cheque") se recompone en backend con '/'. */}
            {CHEQUE_FIELDS.map((fname, i) => {
              const val = hitoEscritura[fname];
              const valPrev = i === 0 ? null : hitoEscritura[CHEQUE_FIELDS[i - 1]];
              const habilitado =
                i === 0 || (valPrev != null && valPrev !== "" && Number(valPrev) > 0);
              const label =
                i === 0 ? "Importe cheque/transf." : `Importe cheque/transf. ${i + 1}`;
              if (disabledEscritura) {
                // Solo lectura: mostrar el 1º y los que tengan valor.
                if (i > 0 && (val == null || val === "")) return null;
                return (
                  <S.FieldGroup key={fname}>
                    <p>{label}</p>
                    <p className="value">{formatImporte(val)}</p>
                  </S.FieldGroup>
                );
              }
              // Edición: se muestran los 6, deshabilitando los que aún no tocan.
              return (
                <InputNumber
                  key={fname}
                  field={{
                    label: i === 0 ? requiredLabel(label) : label,
                    name: fname,
                    step: "0.01",
                    min: "0",
                  }}
                  value={val != null ? String(val) : ""}
                  disabled={!habilitado}
                  handleChange={(name, value) =>
                    handleChequeChange(TIPO_HITO.ESCRITURA, i, value)
                  }
                />
              );
            })}
          </S.Row>
          <S.Row>
            {/* DNE2E-131 (T1.3): Clausula CCPP y Clausula IBI (catalogos existentes) */}
            <InputList
              field={{
                label: "Cláusula CCPP",
                name: "clausulaCCPP",
                list: "clausulaCCPP",
              }}
              value={hitoEscritura.clausulaCCPP || ""}
              options={clausulasCCPP}
              handleChange={(field, value) =>
                handleHitoListChange(TIPO_HITO.ESCRITURA, field, value)
              }
              isClearable={true}
              disabled={disabledEscritura}
            />
            <InputList
              field={{
                label: "Cláusula IBI",
                name: "clausulaIBI",
                list: "clausulaIBI",
              }}
              value={hitoEscritura.clausulaIBI || ""}
              options={clausulasIBI}
              handleChange={(field, value) =>
                handleHitoListChange(TIPO_HITO.ESCRITURA, field, value)
              }
              isClearable={true}
              disabled={disabledEscritura}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoEscritura.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(TIPO_HITO.ESCRITURA, name, value)
            }
            disabled={disabledEscritura}
          />
          {/* DNE2E-131 (T3.1): documentos del hito Escritura (copia simple / cheque).
              El listado esta siempre visible (para consultar lo subido, incluso tras completar el hito);
              solo el alta se habilita cuando el hito Escritura es editable (Confirmación de Firma completada). */}
          {renderDocsRequeridos(hitoEscrituraReal, TIPO_HITO.ESCRITURA)}
          <ModuleTareaDocsReserva
            data={docsDeHito(hitoEscrituraReal?.id, DOC_TIPOS_ESCRITURA)}
            metadata={METADATA_DOCS_RESERVA}
            title="Documentos"
            requestAdd={
              !disabledEscritura ? () => setDocModal("escritura") : undefined
            }
            requestDelete={
              !disabledEscritura ? (doc) => setDocDelete(doc) : undefined
            }
            refreshDocs={() => onHitoSaved && onHitoSaved()}
            setLoading={() => {}}
            access={access}
          />
          {renderSubsanacion(hitoEscrituraReal, TIPO_HITO.ESCRITURA, disabledEscritura)}
          {renderBotonesHito(TIPO_HITO.ESCRITURA, disabledEscritura, hitoEscrituraReal)}
        </S.SectionBox>

        {/* ---- Anulación y subsanación escritura (Opcional) ---- */}
        {/* Hasta que Escritura no este completada, se representa como si no existiera
            (sin badge, seccion atenuada), aunque el registro ya exista en BD. */}
        <S.SectionBox
          className={
            !hitoAnulacionReal || !escrituraCompletada ? "empty-section" :
            hitoAnulacion.completado ? "completed-section" : ""
          }
        >
                    <S.SectionBoxHeader>
                      <span>Anulación y subsanación escritura (Opcional)</span>
                      {renderHitoStatusBadge(TIPO_HITO.ANULACION_SUBSANACION, escrituraCompletada ? hitoAnulacionReal : null)}
                    </S.SectionBoxHeader>
          {(!hitoAnulacionReal || !escrituraCompletada) && (
            <p className="empty-msg">Se habilitará una vez completado el hito de Escritura.</p>
          )}
          <S.Row>
            <InputList
              field={{
                label: requiredLabel("Subsanar"),
                name: "subsanar",
                list: "subsanar",
              }}
              value={hitoAnulacion.subsanar || ""}
              options={FAKE_OPTIONS.subsanar}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
            <InputDate
              field={{
                label: hitoAnulacion.subsanar === "1" ? requiredLabel("Fecha subsanada") : "Fecha subsanada",
                name: "fechaSubsanada",
                type: "datetime-local",
              }}
              value={hitoAnulacion.fechaSubsanada || ""}
              handleChange={(name, value) =>
                handleHitoChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  name,
                  value
                )
              }
              disabled={disabledAnulacion}
            />
          </S.Row>
          <S.Row>
            <InputList
              field={{
                label: requiredLabel("Tributación escritura"),
                name: "tributacionEscritura",
                list: "tributacionEscritura",
              }}
              value={hitoAnulacion.tributacionEscritura || ""}
              options={FAKE_OPTIONS.tributacionEscritura}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
            <InputList
              field={{
                label: requiredLabel("Financiación"),
                name: "financiacion",
                list: "financiacion",
              }}
              value={hitoAnulacion.financiacion || ""}
              options={FAKE_OPTIONS.financiacion}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
          </S.Row>
          <S.Row>
            <InputList
              field={{
                label: requiredLabel("Retención prov. fondos"),
                name: "retencionProvisionFondos",
                list: "retencionProvisionFondos",
              }}
              value={hitoAnulacion.retencionProvisionFondos || ""}
              options={FAKE_OPTIONS.retencionProvisionFondos}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
            <InputList
              field={{
                label: requiredLabel("Cláusula IBI"),
                name: "clausulaIBI",
                list: "clausulaIBI",
              }}
              value={hitoAnulacion.clausulaIBI || ""}
              options={clausulasIBI}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
          </S.Row>
          <S.Row>
            <InputList
              field={{
                label: requiredLabel("Cláusula CCPP"),
                name: "clausulaCCPP",
                list: "clausulaCCPP",
              }}
              value={hitoAnulacion.clausulaCCPP || ""}
              options={clausulasCCPP}
              handleChange={(field, value) =>
                handleHitoListChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  field,
                  value
                )
              }
              isClearable={true}
              disabled={disabledAnulacion}
            />
            <InputText
              field={{
                label: requiredLabel("Gestoría cancelación"),
                name: "gestoriaTramiteCancelacion",
              }}
              value={hitoAnulacion.gestoriaTramiteCancelacion || ""}
              handleChange={(name, value) =>
                handleHitoChange(
                  TIPO_HITO.ANULACION_SUBSANACION,
                  name,
                  value
                )
              }
              disabled={disabledAnulacion}
            />
          </S.Row>
          <InputTextArea
            field={{ label: "Observaciones", name: "observaciones" }}
            rows={2}
            value={hitoAnulacion.observaciones || ""}
            handleChange={(name, value) =>
              handleHitoChange(
                TIPO_HITO.ANULACION_SUBSANACION,
                name,
                value
              )
            }
            disabled={disabledAnulacion}
          />
          {hitoAnulacionReal && escrituraCompletada && (
            <S.HitoButtons>
              {!hitoAnulacion.completado && (
                <>
                  <Button
                    label={saving === TIPO_HITO.ANULACION_SUBSANACION ? "Completando..." : "Completar"}
                    onClick={() => {
                      const hito = getHitoByTipo(hitosState, TIPO_HITO.ANULACION_SUBSANACION);
                      if (hito) {
                        const errores = validarCamposParaCompletar(TIPO_HITO.ANULACION_SUBSANACION, hito);
                        if (errores.length > 0) {
                          errores.forEach((err) => toast.error(err));
                          return;
                        }
                      }
                      setConfirmCompletarHito(TIPO_HITO.ANULACION_SUBSANACION);
                    }}
                    disabled={
                      disabledAnulacion ||
                      hitoAnulacion.completado ||
                      !puedeCompletar(TIPO_HITO.ANULACION_SUBSANACION, hitosState) ||
                      saving === TIPO_HITO.ANULACION_SUBSANACION
                    }
                  />
                  <Button
                    label="Subsanar Escritura"
                    onClick={() => setShowSubsanacionModal(true)}
                    disabled={!escrituraCompletada || disabled}
                  />
                </>
              )}
            </S.HitoButtons>
          )}
        </S.SectionBox>

        {/* ---- Modal de confirmación de completar hito ---- */}
        {confirmCompletarHito !== null && (
          <S.ModalOverlay onClick={() => setConfirmCompletarHito(null)}>
            <S.ModalContent onClick={(e) => e.stopPropagation()}>
              <S.ModalHeader>Confirmar completar hito</S.ModalHeader>
              <S.ModalBody>
                <p>¿Está seguro de que desea completar el hito <strong>{NOMBRE_HITO[confirmCompletarHito] || ""}</strong>? Esta acción no se puede deshacer.</p>
                {confirmCompletarHito === TIPO_HITO.ANULACION_SUBSANACION && (
                  <p><strong>Este proceso anulará la reserva.</strong></p>
                )}
                <S.ModalButtons>
                  <Button
                    label="Sí, completar"
                    onClick={() => {
                      const idTipo = confirmCompletarHito;
                      setConfirmCompletarHito(null);
                      guardarHito(idTipo, true);
                    }}
                  />
                  <Button
                    label="Cancelar"
                    onClick={() => setConfirmCompletarHito(null)}
                    type="cancel"
                  />
                </S.ModalButtons>
              </S.ModalBody>
            </S.ModalContent>
          </S.ModalOverlay>
        )}

        {/* ---- Modal de subsanación ---- */}
        {showSubsanacionModal && (
          <S.ModalOverlay onClick={() => setShowSubsanacionModal(false)}>
            <S.ModalContent onClick={(e) => e.stopPropagation()}>
              <S.ModalHeader>Subsanar Escritura</S.ModalHeader>
              <S.ModalBody>
                <p>¿Desea subsanar la escritura? Se habilitarán los campos del hito de escritura para corregir datos erróneos.</p>
                <S.ModalButtons>
                  <Button
                    label="Sí, subsanar"
                    onClick={() => {
                      setSubsanacionMode("modificar");
                      setShowSubsanacionModal(false);
                    }}
                  />
                  <Button
                    label="Cancelar"
                    onClick={() => setShowSubsanacionModal(false)}
                    type="cancel"
                  />
                </S.ModalButtons>
              </S.ModalBody>
            </S.ModalContent>
          </S.ModalOverlay>
        )}

        {/* ---- Modales de alta de documento por hito (DNE2E-131) ---- */}
        {docModal === "escritura" && (
          <ModalDocumentos
            type="addDocument"
            title="Nuevo documento (Escritura)"
            labelConfirm="Guardar"
            lists={listsParaHito(DOC_TIPOS_ESCRITURA)}
            documentsListName="TipoDocumentoReserva"
            confirm={addDocumentoHito}
            cancel={() => setDocModal(null)}
          />
        )}
        {docModal === "contratoPrivado" && (
          <ModalDocumentos
            type="addDocument"
            title="Nuevo documento (Contrato Privado)"
            labelConfirm="Guardar"
            lists={listsParaHito(DOC_TIPOS_CONTRATO_PRIVADO)}
            documentsListName="TipoDocumentoReserva"
            confirm={addDocumentoHito}
            cancel={() => setDocModal(null)}
          />
        )}
        {docDelete && (
          <Modal
            type="deleteFilter"
            title="Eliminar documento"
            message={`¿Seguro que deseas eliminar el documento "${
              docDelete.nombreDocumento || ""
            }"? Esta acción no se puede deshacer.`}
            labelConfirm="Eliminar"
            confirm={confirmarEliminarDoc}
            cancel={() => setDocDelete(null)}
          />
        )}
      </S.Content>
    </S.Wrapper>
  );
};

ModuleDatosPostFirma.propTypes = {
  hitos: PropTypes.array,
  datosGenerales: PropTypes.object,
  access: PropTypes.object,
  idTarea: PropTypes.string,
  onHitoSaved: PropTypes.func,
  guardarTodosRef: PropTypes.object,
};
